import { Link } from "react-router-dom";
import { ArrowRight, Building2, Check, Clock3, ConciergeBell, Plane } from "lucide-react";
import { TierPill } from "@/components/Brand";
import { ScanToBook } from "@/components/ScanToBook";
import { Section } from "@/components/layout/Page";
import { TripCard, TripCardSkeleton } from "@/components/TripCard";
import { useAuth } from "@/context/AuthContext";
import { useBookings } from "@/hooks/queries";
import { SERVICE_LABELS, VEHICLE_IMAGES } from "@/lib/booking";
import { fareLabel, fmtLongDay, fmtTime, greeting, shortAddress } from "@/lib/format";

const QUICK = [
  { type: "airport", label: "Airport", sub: "Hartsfield-Jackson", icon: Plane },
  { type: "hourly", label: "Hourly", sub: "Chauffeur on call", icon: Clock3 },
  { type: "transfer", label: "City", sub: "Point to point", icon: Building2 },
];

const PERKS = ["Book your luxury transportation", "Request service", "Get connected with Luxury Elite ATL"];

function NextRide({ booking }) {
  return (
    <div data-testid="hero-next-ride">
      <p className="eyebrow text-gold">Your next ride · {SERVICE_LABELS[booking.service_type]}</p>
      <h2 className="mt-3 text-3xl leading-tight tracking-tight text-ivory sm:text-4xl">{fmtLongDay(booking.scheduled_at)}</h2>
      <p className="mt-1 font-display text-xl text-gold-soft">{fmtTime(booking.scheduled_at)}</p>
      <div className="glass mt-5 rounded-2xl p-4 text-sm">
        <p className="truncate text-ivory"><span className="mr-2 inline-block h-2 w-2 rounded-full bg-gold" />{shortAddress(booking.pickup.address)}</p>
        <p className="mt-2 truncate text-mist">
          <span className="mr-2 inline-block h-2 w-2 rounded-full border border-gold bg-ivory" />
          {booking.dropoff ? shortAddress(booking.dropoff.address) : `${booking.hours} hours, as directed`}
        </p>
        <div className="mt-3 flex items-center justify-between border-t border-line pt-3 text-xs text-mist">
          <span>{booking.vehicle_name} · {booking.vehicle_model}</span>
          <span className="font-display text-base text-ivory">{fareLabel(booking)}</span>
        </div>
      </div>
      <Link to={`/trips/${booking.id}`} className="btn-gold mt-5" data-testid="hero-view-ride-btn">
        View ride details <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function BookCta() {
  return (
    <div data-testid="hero-book-cta" className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
      <div>
      <p className="eyebrow text-gold" data-testid="hero-tagline">Luxury is one scan away.</p>
      <h2 className="mt-3 max-w-md text-4xl leading-[1.05] tracking-tight text-ivory sm:text-5xl">Atlanta, your luxury ride awaits.</h2>
      <p className="mt-3 max-w-sm text-sm text-mist">Luxury Elite ATL is now accepting bookings.</p>
      <p className="mt-1 font-display text-lg text-gold-soft">Professional. Private. Reliable.</p>
      <ul className="mt-4 space-y-1.5 text-sm text-ivory" data-testid="hero-perks">
        {PERKS.map((p) => (
          <li key={p} className="flex items-center gap-2"><Check className="h-4 w-4 text-gold" /> {p}</li>
        ))}
      </ul>
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Link to="/book" className="btn-gold" data-testid="hero-book-ride-btn">
          Book a Ride <ArrowRight className="h-4 w-4" />
        </Link>
        <a href="tel:+16784867374" className="text-sm text-ivory hover:text-gold" data-testid="hero-booking-phone">
          <span className="eyebrow mr-2 text-gold">Booking</span>678-486-7374
        </a>
      </div>
      </div>
      <ScanToBook className="hidden shrink-0 sm:flex" testId="hero-scan-to-book" />
    </div>
  );
}

function HeroCard({ booking, loading }) {
  const img = booking ? VEHICLE_IMAGES[booking.vehicle_id] : "/images/brand-escalade.jpg";
  return (
    <section data-testid="hero-card" className="rise stagger-1 relative overflow-hidden rounded-3xl border border-line lg:col-span-2">
      <img src={img} alt="" className="slow-zoom absolute inset-0 h-full w-full object-cover object-[30%_center] sm:object-center" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-transparent sm:via-ink/85 sm:to-ink/20" />
      <div className="relative flex min-h-[380px] flex-col justify-end p-6 sm:p-8 lg:min-h-[440px]">
        {loading ? <div className="h-40 animate-pulse rounded-2xl bg-elevated/50" /> : booking ? <NextRide booking={booking} /> : <BookCta />}
      </div>
    </section>
  );
}

function ConciergePromo() {
  return (
    <Link
      to="/concierge"
      data-testid="concierge-promo-card"
      className="lux-card rise stagger-2 group relative flex flex-col justify-between overflow-hidden p-6 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold/40 sm:p-7"
    >
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gold/10 blur-3xl" />
      <div>
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/30 bg-gold/10 text-gold">
          <ConciergeBell className="h-5 w-5" strokeWidth={1.6} />
        </span>
        <p className="eyebrow mt-6">Dedicated concierge</p>
        <h3 className="mt-2 text-2xl leading-snug text-ivory">Dinner, events and every arrival — arranged by message.</h3>
      </div>
      <span className="mt-8 inline-flex items-center gap-2 text-sm text-gold">
        Message your concierge <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { data: upcoming = [], isLoading } = useBookings("upcoming");
  return (
    <div className="space-y-10">
      <div className="rise flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{greeting()},</p>
          <h1 className="mt-2 text-4xl tracking-tight text-ivory sm:text-5xl lg:text-6xl" data-testid="home-greeting">
            {user.name.split(" ")[0]}<span className="text-gold">.</span>
          </h1>
        </div>
        <div className="hidden lg:block">
          <TierPill tier={user.membership_tier} testId="home-membership-tier" />
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <HeroCard booking={upcoming[0]} loading={isLoading} />
        <ConciergePromo />
      </div>

      <Section title="Reserve in a tap">
        <div className="grid grid-cols-3 gap-3 sm:gap-5">
          {QUICK.map((q, i) => (
            <Link
              key={q.type}
              to={`/book?type=${q.type}`}
              data-testid={`quick-book-${q.type}`}
              className={`lux-card rise stagger-${i + 2} group flex flex-col items-start gap-4 p-4 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold/40 sm:p-6`}
            >
              <q.icon className="h-6 w-6 text-gold transition-transform duration-300 group-hover:-translate-y-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-display text-lg text-ivory sm:text-xl">{q.label}</p>
                <p className="hidden text-xs text-mist sm:block">{q.sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section
        title="Upcoming itinerary"
        aside={<Link to="/trips" className="text-sm text-gold hover:underline" data-testid="home-view-all-trips-link">View all</Link>}
      >
        <div className="space-y-3" data-testid="home-itinerary">
          {isLoading && [0, 1].map((i) => <TripCardSkeleton key={i} />)}
          {!isLoading && upcoming.length === 0 && (
            <div className="lux-card p-6 text-sm text-mist" data-testid="home-itinerary-empty">
              Nothing scheduled yet. Your reserved rides will appear here.
            </div>
          )}
          {upcoming.slice(0, 3).map((b, i) => <TripCard key={b.id} booking={b} index={i} />)}
        </div>
      </Section>
    </div>
  );
}
